import { useDisplayCurrency } from '../../hooks/useCurrency';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchPayouts,
  syncPayouts,
  triggerPayout,
  fetchDistributions,
  createDistribution,
  approveDistribution,
  markDistributionPaid,
  cancelDistribution,
} from '../../store/actions/FinanceActions';
import Table from '../shared/TailwindComponents/Table';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Textarea from '../shared/TailwindComponents/Textarea';
import ModalShell from '../shared/ModalShell';
import {
  PiArrowsClockwiseLight,
  PiArrowUpLight,
  PiBankLight,
  PiCheckLight,
  PiCurrencyDollarLight,
  PiXCircleLight,
  PiPlusLight,
  PiUsersLight,
} from 'react-icons/pi';
import { FinancePayout, FinanceDistribution } from '../../store/reducer/FinanceReducer';
import { useOrgMembers } from '../../hooks/useOrgMembers';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const statusBadgeColor: Record<string, any> = {
  paid: 'green',
  pending: 'yellow',
  in_transit: 'blue',
  failed: 'red',
  canceled: 'gray',
};

const distStatusColor: Record<string, any> = {
  pending: 'yellow',
  approved: 'blue',
  paid: 'green',
  cancelled: 'gray',
};

const distTypeLabels: Record<string, string> = {
  salary: 'Salary',
  bonus: 'Bonus',
  matter_fee: 'Matter Fee',
  referral: 'Referral',
  expense_reimbursement: 'Expense Reimbursement',
};

const PayoutsTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payouts = useAppSelector((state: RootState) => state.finance.payouts);
  const distributions = useAppSelector((state: RootState) => state.finance.distributions);
  const balance = useAppSelector((state: RootState) => state.finance.balance);
  const account = useAppSelector((state: RootState) => state.finance.account);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  // One currency rule app-wide (hooks/useCurrency): the user's, else the firm's.
  const preferredCurrency = useDisplayCurrency();
  const [page, setPage] = useState(1);
  const [distPage, setDistPage] = useState(1);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [showDistributionForm, setShowDistributionForm] = useState(false);
  const [activeView, setActiveView] = useState<'payouts' | 'distributions'>('payouts');

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchPayouts(organizationId, { page }));
    }
  }, [dispatch, organizationId, page]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchDistributions(organizationId, { page: distPage }));
    }
  }, [dispatch, organizationId, distPage]);

  // Listen for header "Manual Payout" button
  useEffect(() => {
    const handler = () => setShowPayoutForm(true);
    window.addEventListener('finance:manual-payout', handler);
    return () => window.removeEventListener('finance:manual-payout', handler);
  }, []);

  const handleSync = async () => {
    if (!organizationId) return;
    await dispatch(syncPayouts(organizationId));
    dispatch(fetchPayouts(organizationId, { page: 1 }));
    setPage(1);
  };

  const handleApproveDistribution = async (id: number) => {
    if (!organizationId) return;
    await dispatch(approveDistribution(id, organizationId));
    dispatch(fetchDistributions(organizationId, { page: distPage }));
  };

  const handleMarkPaid = async (id: number) => {
    if (!organizationId) return;
    await dispatch(markDistributionPaid(id, organizationId));
    dispatch(fetchDistributions(organizationId, { page: distPage }));
  };

  const handleCancelDistribution = async (id: number) => {
    if (!organizationId) return;
    await dispatch(cancelDistribution(id, organizationId));
    dispatch(fetchDistributions(organizationId, { page: distPage }));
  };

  const availableBalance = balance?.available?.[0]?.amount || 0;
  const pendingBalance = balance?.pending?.[0]?.amount || 0;
  const currency = account?.default_currency || preferredCurrency;

  // Compute payouts summary
  const payoutsSummary = React.useMemo(() => {
    const totalPaid = payouts.items
      .filter((p: FinancePayout) => p.status === 'paid')
      .reduce((sum: number, p: FinancePayout) => sum + p.amount, 0);
    const pendingPayouts = payouts.items
      .filter((p: FinancePayout) => ['pending', 'in_transit'].includes(p.status))
      .reduce((sum: number, p: FinancePayout) => sum + p.amount, 0);
    return { totalPaid, pendingPayouts };
  }, [payouts.items]);

  const columns = ['Date', 'Amount', 'Status', 'Method', 'Arrival', 'Description'];

  const tableData = payouts.items.map((p: FinancePayout) => ({
    id: p.id,
    Date: new Date(p.created_at).toLocaleDateString(),
    Amount: p.amount,
    Status: p.status,
    Method: p.method || '—',
    Arrival: p.arrival_date ? new Date(p.arrival_date).toLocaleDateString() : '—',
    Description: p.description || '—',
    _currency: p.currency,
    _raw: p,
  }));

  const renderCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Amount':
        return <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(value, row._currency)}</span>;
      case 'Status':
        return (
          <Badge color={statusBadgeColor[value] || 'gray'}>
            {value}
          </Badge>
        );
      case 'Description':
        return (
          <span className="text-gray-500 dark:text-gray-400 max-w-xs truncate block">
            {value}
            {row._raw.failure_message && (
              <span className="text-danger-500 ms-1">({row._raw.failure_message})</span>
            )}
          </span>
        );
      default:
        return value;
    }
  };

  return (
    <div className="flex flex-col">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 px-4 pt-4">
        <SummaryCard
          label={t('Available for payout')}
          value={formatCurrency(availableBalance, currency)}
          highlight
        />
        <SummaryCard
          label={t('Total paid out')}
          value={formatCurrency(payoutsSummary.totalPaid, currency)}
        />
        <SummaryCard
          label={t('Pending / In transit')}
          value={formatCurrency(payoutsSummary.pendingPayouts + pendingBalance, currency)}
        />
      </div>

      {/* Manual payout modal */}
      <ManualPayoutModal
        isOpen={showPayoutForm}
        organizationId={organizationId!}
        maxAmount={availableBalance}
        currency={currency}
        onClose={() => setShowPayoutForm(false)}
        onSuccess={() => {
          setShowPayoutForm(false);
          dispatch(fetchPayouts(organizationId!, { page: 1 }));
          setPage(1);
        }}
      />

      {/* Distribution modal */}
      <DistributionModal
        isOpen={showDistributionForm}
        organizationId={organizationId!}
        currency={currency}
        onClose={() => setShowDistributionForm(false)}
        onSuccess={() => {
          setShowDistributionForm(false);
          dispatch(fetchDistributions(organizationId!, { page: 1 }));
          setDistPage(1);
        }}
      />

      {/* Toggle + Action buttons */}
      <div className="flex items-center justify-between px-4 mt-4 mb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView('payouts')}
            className={`flex items-center px-3 py-1 text-xs font-medium rounded nav-pill ${
              activeView === 'payouts'
                ? 'nav-pill-active text-primary-600 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {t('Payouts')}
          </button>
          <button
            onClick={() => setActiveView('distributions')}
            className={`flex items-center px-3 py-1 text-xs font-medium rounded nav-pill ${
              activeView === 'distributions'
                ? 'nav-pill-active text-primary-600 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {t('Distributions')}
          </button>
        </div>

        {activeView === 'distributions' && (
          <Button variant="outline" size="small" onClick={() => setShowDistributionForm(true)}>
            <PiPlusLight className="w-4 h-4 me-1" />
            {t('New distribution')}
          </Button>
        )}
      </div>

      {/* ── Payouts View ──────────────────────────────────── */}
      {activeView === 'payouts' && (
        <>
          <Table
            columns={columns}
            data={tableData}
            renderCell={renderCell}
            min="min-h-0"
            emptyState={{
              icon: <PiBankLight className="w-10 h-10" />,
              headline: t('No payouts yet'),
            }}
          />

          {payouts.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('Page')} {payouts.page} {t('of')} {payouts.total_pages} · {payouts.total} {t('total')}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {t('Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setPage((p) => Math.min(payouts.total_pages, p + 1))}
                  disabled={page >= payouts.total_pages}
                >
                  {t('Next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Distributions View ────────────────────────────── */}
      {activeView === 'distributions' && (
        <DistributionsTable
          distributions={distributions}
          distPage={distPage}
          setDistPage={setDistPage}
          onApprove={handleApproveDistribution}
          onMarkPaid={handleMarkPaid}
          onCancel={handleCancelDistribution}
        />
      )}
    </div>
  );
};

// ── Summary Card ────────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="glass rounded-lg p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-medium mt-1 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
      {value}
    </p>
  </div>
);

// ── Manual Payout Modal ─────────────────────────────────────────

const ManualPayoutModal: React.FC<{
  isOpen: boolean;
  organizationId: number;
  maxAmount: number;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, organizationId, maxAmount, currency, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setDescription('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);

    if (!amountCents || amountCents <= 0 || amountCents > maxAmount) return;

    const result = await dispatch(
      triggerPayout({
        organization_id: organizationId,
        amount: amountCents,
        currency,
        description: description || undefined,
      })
    );

    if (result) {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t('Manual payout')}
      size="sm"
      zIndexClassName="z-[9999999]"
      footer={
        <>
          <Button variant="outline" size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            type="submit"
            form="manual-payout-form"
            isLoading={loading?.triggerPayout}
          >
            {t('Initiate payout')}
          </Button>
        </>
      }
    >
        <form id="manual-payout-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Input
                label={`${t('Amount')} (${currency.toUpperCase()})`}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('Max')}: {formatCurrency(maxAmount, currency)}
              </p>
            </div>
            <Input
              label={`${t('Description')} (${t('optional')})`}
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('Payout description...')}
            />
          </div>
        </form>
    </ModalShell>
  );
};

// ── Distributions Table ─────────────────────────────────────────

const DistributionsTable: React.FC<{
  distributions: { items: FinanceDistribution[]; page: number; total_pages: number; total: number };
  distPage: number;
  setDistPage: React.Dispatch<React.SetStateAction<number>>;
  onApprove: (id: number) => void;
  onMarkPaid: (id: number) => void;
  onCancel: (id: number) => void;
}> = ({ distributions, distPage, setDistPage, onApprove, onMarkPaid, onCancel }) => {
  const { t } = useTranslation();

  const distColumns = ['Date', 'Type', 'Amount', 'Status', 'Notes'];

  const distTableData = distributions.items.map((d: FinanceDistribution) => ({
    id: d.id,
    Date: new Date(d.created_at).toLocaleDateString(),
    Type: d.type,
    Amount: d.amount,
    Status: d.status,
    Notes: d.notes || '—',
    _currency: d.currency,
    _raw: d,
  }));

  const renderDistCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Type':
        return (
          <span className="text-gray-700 dark:text-gray-300 capitalize">
            {t(distTypeLabels[value] || (value || '').replace(/_/g, ' '))}
          </span>
        );
      case 'Amount':
        return (
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(value, row._currency)}
          </span>
        );
      case 'Status':
        return (
          <Badge color={distStatusColor[value] || 'gray'}>
            {(value || '').replace(/_/g, ' ')}
          </Badge>
        );
      case 'Notes':
        return (
          <span className="text-gray-500 dark:text-gray-400 max-w-xs truncate block">
            {value}
          </span>
        );
      default:
        return value;
    }
  };

  const distActions = [
    {
      label: t('Approve'),
      icon: <PiCheckLight className="w-4 h-4" />,
      onClick: (row: any) => onApprove(row.id),
      disabled: (row: any) => row._raw.status !== 'pending',
    },
    {
      label: t('Mark paid'),
      icon: <PiCurrencyDollarLight className="w-4 h-4" />,
      onClick: (row: any) => onMarkPaid(row.id),
      disabled: (row: any) => row._raw.status !== 'approved',
    },
    {
      label: t('Cancel'),
      icon: <PiXCircleLight className="w-4 h-4" />,
      onClick: (row: any) => onCancel(row.id),
      disabled: (row: any) => !['pending', 'approved'].includes(row._raw.status),
      isDestructive: true,
    },
  ];

  return (
    <>
      <Table
        columns={distColumns}
        data={distTableData}
        renderCell={renderDistCell}
        actions={distActions}
        min="min-h-0"
        emptyState={{
          icon: <PiUsersLight className="w-10 h-10" />,
          headline: t('No distributions yet'),
        }}
      />
      {distributions.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('Page')} {distributions.page} {t('of')} {distributions.total_pages} · {distributions.total} {t('total')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="small"
              onClick={() => setDistPage((p) => Math.max(1, p - 1))}
              disabled={distPage <= 1}
            >
              {t('Previous')}
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => setDistPage((p) => Math.min(distributions.total_pages, p + 1))}
              disabled={distPage >= distributions.total_pages}
            >
              {t('Next')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// ── Distribution Modal ─────────────────────────────────────────

const DistributionModal: React.FC<{
  isOpen: boolean;
  organizationId: number;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, organizationId, currency, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const { members: orgMembers, loading: membersLoading } = useOrgMembers();

  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('salary');
  const [notes, setNotes] = useState('');

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setUserId('');
      setAmount('');
      setType('salary');
      setNotes('');
    }
  }, [isOpen]);

  const typeOptions = Object.entries(distTypeLabels).map(([value, label]) => ({
    value,
    label: t(label),
  }));

  const memberOptions = orgMembers.map(m => ({
    value: m.id,
    label: `${m.name}${m.role ? ` (${m.role})` : ''}`,
    avatarUrl: m.avatarUrl,
    email: m.email,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!amountCents || amountCents <= 0 || !userId) return;

    const result = await dispatch(
      createDistribution({
        organization_id: organizationId,
        user_id: parseInt(userId),
        amount: amountCents,
        currency,
        type,
        notes: notes || undefined,
      })
    );

    if (result) {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t('New distribution')}
      size="md"
      zIndexClassName="z-[9999999]"
      footer={
        <>
          <Button variant="outline" size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            type="submit"
            form="distribution-form"
            isLoading={loading?.createDistribution}
          >
            {t('Create distribution')}
          </Button>
        </>
      }
    >
        <form id="distribution-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Dropdown
              options={memberOptions}
              value={userId}
              onChange={(val) => setUserId(val as string)}
              label={t('Team member')}
              placeholder={membersLoading ? t('Loading members...') : t('Select team member')}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={`${t('Amount')} (${currency.toUpperCase()})`}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              <Dropdown
                options={typeOptions}
                value={type}
                onChange={(val) => setType(val as string)}
                label={t('Type')}
              />
            </div>

            <Textarea
              id="distribution-notes"
              label={`${t('Notes')} (${t('optional')})`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('Distribution notes...')}
              rows={2}
            />
          </div>
        </form>
    </ModalShell>
  );
};

export default PayoutsTab;
